import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import Select from '../../../../components/Select'
import MultiFieldItem from '../MultiFieldItem'
import ManualSearchableSelect from '../ManualSearchableSelect'
import SelectPaginated from '../../../../components/SelectPaginated'

import { capitalizeWords } from '../../../../helpers/common'

// MultiSelectBox - is a Select component with possibility to select multiple values, but with UI differences:
// Instead of rendering selected values in the Select body as Chip - values renders as Pseudo Input(visually
// similar as InputField) elements.
// Also it allows to make BE or FE search within <Select/> component
function MultiSelectBox({
  name,
  setFieldValue,
  value,
  className,
  valueClassName,
  searchAction,
  onSearch,
  placeholder,
  noOptionsMessageText,
  CustomMultiFieldItem,
  onValueChangeCallback,
  renderSelectedValues = true,
  disablePlaceholderFormatting,
  ...props
}) {
  const { t } = useTranslation()

  const formattedPlaceholder = useMemo(() => {
    if (placeholder) {
      return disablePlaceholderFormatting ? t(placeholder) : capitalizeWords(t(placeholder))
    } else {
      return t('Select...')
    }
  }, [disablePlaceholderFormatting, placeholder, t])

  const onValueChange = useCallback(
    values => {
      if (onValueChangeCallback) {
        onValueChangeCallback(values)
      } else {
        setFieldValue(name, values)
      }
    },
    [onValueChangeCallback, setFieldValue, name]
  )

  const onSelectedValueRemove = removeValue => {
    onValueChange(value.filter(item => item.value !== removeValue))
  }

  const selectedValues = value.map(({ value, label }, index) => {
    if (CustomMultiFieldItem) {
      return React.cloneElement(CustomMultiFieldItem, {
        name,
        index,
        value,
        key: value,
        label,
        className: valueClassName,
        deleteHandler: onSelectedValueRemove
      })
    } else {
      return (
        <MultiFieldItem
          value={value}
          key={value}
          label={label}
          className={valueClassName}
          deleteHandler={onSelectedValueRemove}
        />
      )
    }
  })

  const noOptionsMessage = useCallback(() => t(noOptionsMessageText || 'No Results'), [t, noOptionsMessageText])

  const isManuallySearched = !!searchAction || !!onSearch

  const commonMultiSelectProps = useMemo(
    () => ({
      isMulti: true,
      value: value,
      onChange: onValueChange,
      controlShouldRenderValue: false,
      formatValue: false,
      backspaceRemovesValue: false,
      placeholder: formattedPlaceholder
    }),
    [value, onValueChange, formattedPlaceholder]
  )

  const renderSelectComponent = useCallback(() => {
    const loadOptions = props.loadOptions

    if (loadOptions) {
      return <SelectPaginated isSearchable loadOptions={loadOptions} {...commonMultiSelectProps} {...props} />
    } else if (isManuallySearched) {
      return (
        <ManualSearchableSelect
          searchAction={searchAction}
          onSearch={onSearch}
          noOptionsMessageText={noOptionsMessageText}
          {...props}
          {...commonMultiSelectProps}
        />
      )
    } else {
      return (
        <Select
          noOptionsMessage={noOptionsMessage}
          // We don't allow text search if there are only few options
          isSearchable={props.options.length >= 8}
          {...props}
          {...commonMultiSelectProps}
        />
      )
    }
  }, [
    isManuallySearched,
    searchAction,
    onSearch,
    noOptionsMessage,
    noOptionsMessageText,
    props,
    commonMultiSelectProps
  ])

  return (
    <div className={classnames('field', className)}>
      {renderSelectedValues && <div>{selectedValues}</div>}

      {renderSelectComponent()}
    </div>
  )
}

MultiSelectBox.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  valueClassName: PropTypes.string,
  options: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  value: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  searchAction: PropTypes.func,
  onSearch: PropTypes.func,
  noOptionsMessageText: PropTypes.string,
  extraSearchParams: PropTypes.object,
  CustomMultiFieldItem: PropTypes.elementType,
  onValueChangeCallback: PropTypes.func,
  renderSelectedValues: PropTypes.bool,
  placeholder: PropTypes.string,
  disablePlaceholderFormatting: PropTypes.bool
}

export default MultiSelectBox
